import { showToastNotification } from '../ui/toasts/toastsActions';
import { ToastType } from '../ui/toasts/toastsTypes';
import { getErrorMessage } from '../../utils/miscUtil';
import { AnyAction, Dispatch } from 'redux';
import _ from 'lodash';
import {
  activeRecordingPageUpdated,
  expandRecordingRow,
  getRecordings,
  setIsCreatingNewRecording,
  setIsLoadingRecording,
  setRecordingFilter,
} from './recordingsReducer';
import { ApplicationState } from '../rootReducer';
import { BulkOperation, Recording } from '../../types/rest/assetCatalogTypes';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import MixpanelService from '../../utils/mixpanelService';
import {
  CREATE_RECORDING,
  DELETE_RECORDING,
  RECORDING_BULK_OPERATION,
  UPDATE_RECORDING,
} from '../../utils/mixpanelEvents';
import acApi from '../../axios/ac/acApi';
import i18n from '../../i18n';
import { SelectAllType } from '../../components/blazeTable/BlazeTable.types';
import { buildBulkOperationBaseDto, displayErrorForBulkOpsThunk } from '../transactions/transactionsActions';
import { fetchRecordingMessagesThunk } from './recordingMessages/recordingMessagesActions';
import { buildPageableQueryParameters, buildSortingParam } from '../../utils/pagebleUtil';
import store from '../configureStore';
import { PageFilterContent } from '../ui/pageFilters/pageFilterTypes';

export const bulkTagRecordingThunk = (
  recordingIds: number[],
  tags: string[],
  selectAll?: SelectAllType,
): ThunkAction<any, any, any, AnyAction> => {
  return async (dispatch: ThunkDispatch<any, any, AnyAction>) => {
    try {
      dispatch(setIsLoadingRecording(true));
      const bulkTagAction: BulkOperation = buildBulkOperationBaseDto('TAG', recordingIds);
      bulkTagAction.tags = tags;
      const response = await acApi.recordings.tagBulkRecordings(bulkTagAction);
      if (response.data.error) {
        displayErrorForBulkOpsThunk(response, 'Could not tag the selected Recordings.', dispatch);
      }
      MixpanelService.track(RECORDING_BULK_OPERATION('tag', tags.length));
      const txnLength = selectAll === 'ALL' ? 'All' : recordingIds.length;
      return dispatch(
        fetchRecordingsThunk(
          true,
          txnLength === 1
            ? `${txnLength} ${i18n.t('recordings:actions.appliedTagsToRec')}`
            : `${txnLength} ${i18n.t('recordings:actions.appliedTagsToRecs')}`,
        ),
      );
    } catch (e) {
      dispatch(
        showToastNotification(
          ToastType.Danger,
          `${i18n.t('recordings:actions.failedToApplyTagsToRecs')} ${
            e.response ? e.response.data.error : 'Something went wrong.'
          }`,
        ),
      );
      return dispatch(fetchRecordingsThunk(true));
    }
  };
};

export const showRecordingEditorThunk = (isCreating: boolean): ThunkAction<any, ApplicationState, null, AnyAction> => {
  return async (dispatch: ThunkDispatch<ApplicationState, null, AnyAction>): Promise<void> => {
    dispatch(setIsCreatingNewRecording(isCreating));
  };
};

export const fetchRecordingsThunk = (silentReload: boolean, postLoadMessage?: string) => {
  return async (dispatch: Dispatch, getState: any): Promise<void> => {
    const state: ApplicationState = getState() as ApplicationState;
    if (silentReload !== true) {
      dispatch(setIsLoadingRecording(true));
    }
    const searchTerm: string = encodeURIComponent(state.recordings.pageFilter.searchTerm);
    const serviceId = state.recordings.pageFilter.serviceId;
    // Build the request for transactions, optionally filtered by:
    const pageParams = buildPageableQueryParameters(state.recordings.pageable);
    const sortParam = buildSortingParam(state.recordings.sort);
    const params = {
      ...pageParams,
      serviceId: serviceId !== 0 ? serviceId : undefined,
      filter: searchTerm,
      sort: !_.isNil(sortParam) ? sortParam : '-id',
    };

    try {
      const response = await acApi.recordings.getRecordings(params);
      // If we have zero results from backend and there are some results available in total, and we are not at first
      // page, go to previous page
      if (response.data.result.length === 0 && response.data.total !== 0 && state.recordings.pageable.skip !== 1) {
        const previousPage = state.recordings.pageable.skip - 1 || 1;
        await dispatch(updateActiveRecordingPage(previousPage));
      } else {
        const recordings = response.data.result;
        const recordingPage = { recordings: recordings, total: response.data.total, isLoading: false };
        dispatch(getRecordings(recordingPage));
      }

      if (!_.isEmpty(postLoadMessage) && postLoadMessage != null) {
        dispatch(showToastNotification(ToastType.Success, postLoadMessage));
      }
    } catch (e) {
      dispatch(showToastNotification(ToastType.Danger, 'Cannot get list of recordings', getErrorMessage(e)));
    } finally {
      dispatch(setIsLoadingRecording(false));
    }
  };
};

export const createPostRecording = (recordingToCreate: any, recordingName: string) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const params = {
        serviceId: recordingToCreate.serviceId,
      };
      const response = await acApi.recordings.postRecording(recordingToCreate, params);
      dispatch(
        fetchRecordingsThunk(true, `${i18n.t('recordings:actions.successfullyCreatedRec')} ${recordingName}`) as any,
      );
      const newRecordingId = response.data.result.id;
      await dispatch(fetchRecordingsThunk(true) as any);
      dispatch(setIsCreatingNewRecording(false));
      dispatch(expandRecordingRow(newRecordingId));
      MixpanelService.track(CREATE_RECORDING());
    } catch (e) {
      const error = getErrorMessage(e);
      dispatch(showToastNotification(ToastType.Danger, 'Cannot update Recording', error));
    } finally {
    }
  };
};

export const updatePatchRecording = (recordingId: number, recordingPatch: any, recordingName: string) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      await acApi.recordings.patchRecording(recordingId, recordingPatch);
      dispatch(
        fetchRecordingsThunk(true, `${i18n.t('recordings:actions.successfullyUpdatedRec')} ${recordingName}`) as any,
      );
      dispatch(fetchRecordingMessagesThunk(recordingId, true) as any);
      MixpanelService.track(UPDATE_RECORDING());
    } catch (e) {
      const error = getErrorMessage(e);
      dispatch(showToastNotification(ToastType.Danger, 'Cannot update Recording', error));
    } finally {
    }
  };
};

export const updateActiveRecordingPage = (activePage: number): any => {
  return async (dispatch: ThunkDispatch<ApplicationState, null, AnyAction>): Promise<void> => {
    // Update the pagination in the store
    await dispatch(activeRecordingPageUpdated(activePage));
    // Trigger refresh of recordings after update
    await dispatch(fetchRecordingsThunk(false));
  };
};

export const updateRecordingFilterPage = (pageFiltert: PageFilterContent): any => {
  return async (dispatch: ThunkDispatch<ApplicationState, null, AnyAction>, getState: any): Promise<void> => {
    await dispatch(setRecordingFilter(pageFiltert));
    dispatch(fetchRecordingsThunk(true));
  };
};

export const deleteRecordingsBulk = () => {
  return async (dispatch: Dispatch, getState: any): Promise<void> => {
    const state: ApplicationState = getState() as ApplicationState;
    await acApi.recordings.deleteBulkRecordings(state.recordings.selectedRecordings);
    MixpanelService.track(RECORDING_BULK_OPERATION('delete', getSelectedRecordingsLength()));
    dispatch(showToastNotification(ToastType.Success, i18n.t('recordings:actions.deleteBulkSuccess')));
  };
};

export const deleteRecordingById = (recording: Recording) => {
  return async (dispatch: ThunkDispatch<any, any, AnyAction>): Promise<void> => {
    const response = await acApi.recordings.deleteRecording(recording.id);
    if (response.status === 204)
      dispatch(
        showToastNotification(
          ToastType.Success,
          i18n.t('recordings:actions.deleteSingleSuccess', { name: recording.name }),
        ),
      );
    MixpanelService.track(DELETE_RECORDING());
  };
};

export const convertRecordingToTransaction = (recordingId: number, params: any) => {
  return async (dispatch: ThunkDispatch<any, any, AnyAction>): Promise<void> => {
    dispatch(showToastNotification(ToastType.Info, i18n.t('recordings:convertToTxModal.converting')));
    const response = await acApi.recordings.convertToTransaction(recordingId, params);
    if (response.status === 200)
      dispatch(showToastNotification(ToastType.Success, i18n.t('recordings:convertToTxModal.success')));
    MixpanelService.track(DELETE_RECORDING());
  };
};

export const getSelectedRecordingsLength = (): number => {
  const recordings = (store.getState() as ApplicationState).recordings;
  return recordings.selectAllType === 'ALL' ? recordings.totalRecordings : recordings.selectedRecordings.length;
};
